import React, { useState } from 'react';
import { Lineup } from '../data/maps';

interface LineupCardProps {
  lineup: Lineup;
  onClick: () => void;
}

const LineupCard: React.FC<LineupCardProps> = ({ lineup, onClick }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Get all media including images and video
  const allMedia = [...lineup.images, ...(lineup.video ? [lineup.video] : [])];
  const currentMedia = allMedia[currentImageIndex];
  const hasMultipleMedia = allMedia.length > 1;
  const isCurrentMediaVideo = lineup.video && currentMedia === lineup.video;

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleMediaChange = (direction: 'next' | 'prev') => {
    if (!hasMultipleMedia) return;
    
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % allMedia.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
    }
    setImageLoading(true);
    setImageError(false);
  };

  const getSiteColor = (site: string) => {
    switch (site) {
      case 'A':
        return 'bg-red-600 bg-opacity-20 text-red-400';
      case 'B':
        return 'bg-blue-600 bg-opacity-20 text-blue-400';
      case 'Mid':
        return 'bg-green-600 bg-opacity-20 text-green-400';
      default:
        return 'bg-gray-600 bg-opacity-20 text-gray-400';
    }
  };

  return (
    <div
      onClick={onClick}
      className="bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-700 hover:border-yellow-400 cursor-pointer h-80"
    >
      <div className="h-48 bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center relative">
        {/* Loading Skeleton */}
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-full bg-gray-700 animate-pulse rounded-lg">
              <div className="flex items-center justify-center h-full">
                <div className="w-16 h-16 bg-gray-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        )}

        {/* Media Content */}
        {isCurrentMediaVideo ? (
          <video 
            src={currentMedia}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoadStart={() => setImageLoading(true)}
            onLoadedData={() => setImageLoading(false)}
            onError={handleImageError}
            muted
            loop
          />
        ) : (
          <img 
            src={currentMedia} 
            alt={`${lineup.title} - Media ${currentImageIndex + 1}`}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
        
        {/* Media Indicator for Multiple Media */}
        {hasMultipleMedia && !imageLoading && !imageError && (
          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            {currentImageIndex + 1}/{allMedia.length}
          </div>
        )}
        
        {/* Video Play Button Overlay (only when video is not current media) */}
        {lineup.video && !isCurrentMediaVideo && !imageLoading && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity">
            <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center">
              <span className="text-gray-900 text-2xl">▶</span>
            </div>
          </div>
        )}
        
        {/* Fallback Content */}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-900 font-bold text-xl">
                  {lineup.title.split(' ')[0][0]}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{lineup.title}</h3>
              {lineup.description && (
                <p className="text-gray-400 text-sm px-4">{lineup.description}</p>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="p-4 flex flex-col h-32">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-white">{lineup.title}</h3>
          <div className="flex items-center gap-2">
            {lineup.video && (
              <span className="text-yellow-400 text-xs bg-yellow-400 bg-opacity-20 px-2 py-1 rounded">
                📹 Video
              </span>
            )}
            <span className={`text-xs px-2 py-1 rounded ${getSiteColor(lineup.site)}`}>
              {lineup.site}
            </span>
          </div>
        </div>
        {lineup.description && (
          <p className="text-gray-400 text-sm flex-1 overflow-hidden" style={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {lineup.description}
          </p>
        )}
        <p className="text-yellow-400 text-sm font-medium mt-auto">Click to view</p>
      </div>
    </div>
  );
};

export default LineupCard; 